import React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { VenueData, VenueDocument } from "../../types/venues.types";

interface UseVenueDataProps {
  id: string | undefined;
  defaultVenueData?: VenueData;
  apiService: {
    get: <T>(url: string) => Promise<{ data: T }>;
  };
}

interface UseVenueDataReturn {
  venueData: VenueData | null;
  setVenueData: React.Dispatch<React.SetStateAction<VenueData | null>>;
  loading: boolean;
  error: string | null;
}

export const useVenueData = ({
  id,
  defaultVenueData,
  apiService,
}: UseVenueDataProps): UseVenueDataReturn => {
  const [venueData, setVenueData] = useState<VenueData | null>(
    defaultVenueData || null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVenue = async () => {
      if (defaultVenueData) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get<VenueData>(
          `/api/venues/venue/${id}/`,
        );

        const transformedData = {
          ...response.data,
          files: response.data.files.map((file: VenueDocument) => ({
            ...file,
            venue_id: file.venue ?? response.data.id,
          })),
        };
        setVenueData(transformedData);
        toast.success("Venue data loaded successfully!", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      } catch (err) {
        let errorMessage = "Failed to fetch venue data. Please try again.";
        const errorResponse = (
          err as {
            response?: { status?: number; data?: { detail?: string } };
          }
        )?.response;
        if (errorResponse?.status === 404) {
          errorMessage = "Venue not found.";
          navigate("/venues");
        } else if (errorResponse?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (errorResponse?.data?.detail) {
          errorMessage = errorResponse.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
        console.error("Fetch venue error:", errorResponse?.data || err);
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [id, navigate, defaultVenueData, apiService]);

  return { venueData, setVenueData, loading, error };
};

export default useVenueData;
